package com.eladfinish.bible.contest.screens.chapterComplete.usecases

import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.common.random.RandomProvider
import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.ColorConstants.INDIGO_DARK
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.constants.StringConstants.NO_BREAK_SPACE
import com.eladfinish.bible.contest.constants.strings.conjunctionsWords
import com.eladfinish.bible.contest.extensions.FONT_SERIF
import com.eladfinish.bible.contest.extensions.matchesAny
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.CompleteType
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.utils.MyStringUtils.getSeyfaAndColon
import com.eladfinish.bible.contest.utils.MyStringUtils.retrieveVerse
import com.eladfinish.bible.contest.utils.range
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList

class BuildContentByExposureTypeDomainUseCase(
    private val settings: KeyValueSettings,
    private val removeTeamimAndMakafUseCase: RemoveTeamimAndMakafUseCase,
    private val appendBlankWithRevealSpanUseCase: AppendBlankWithRevealSpanUseCase,
    private val randomProvider: RandomProvider,
) {

    private val eachVerseInNewLine by lazy {
        settings.getBoolean(
            key = KEY_EACH_VERSE_IN_NEW_LINE,
            defaultValue = BooleanConstants.ENABLE_EACH_VERSE_IN_NEW_LINE,
        )
    }

    operator fun invoke(
        completeType: CompleteType,
        partitionedChapter: List<List<List<String>>>,
    ): SpannableStringKMP {
        val out = StringBuilder()
        val spans = mutableListOf<SpannedRange>()

        when (completeType) {
            CompleteType.PARTIALLY_EXPOSURE -> {
                for (verseParts in partitionedChapter) {
                    val partsNumber = verseParts.size - 1 // exclude otPasuk
                    val otPasuk = verseParts[0][0]

                    appendOtPasuk(out = out, spans = spans, otPasuk = otPasuk)

                    buildSpannedSegment(
                        out = out,
                        spans = spans,
                        words = verseParts[1],
                    )

                    out.append(" ")

                    if (partsNumber == 3) {
                        buildSpannedSegment(
                            out = out,
                            spans = spans,
                            words = verseParts[2],
                        )
                        out.append(" ")
                    }

                    buildSpannedSegment(
                        out = out,
                        spans = spans,
                        words = verseParts[partsNumber],
                    )

                    appendColon(out = out, newline = eachVerseInNewLine)
                }
            }

            CompleteType.REYSHOT_EXPOSURE -> {
                for (verseParts in partitionedChapter) {
                    val otPasuk = verseParts[0][0]
                    val size = verseParts.size

                    val reysha = retrieveVerse(verseParts[1])
                    var seyfa = retrieveVerse(verseParts[size - 1])
                    if (size == 4) {
                        seyfa = retrieveVerse(verseParts[2]) + " " + seyfa
                    }

                    appendOtPasuk(out = out, spans = spans, otPasuk = otPasuk)
                    out.append(reysha)
                    out.append(" ")

                    appendBlankWithRevealSpanUseCase(
                        out = out,
                        spans = spans,
                        originWord = seyfa,
                        color = INDIGO_DARK,
                        typefaceFamily = FONT_SERIF,
                        appendSpaceAfterMakaf = false,
                    )

                    appendColon(out = out, newline = eachVerseInNewLine)
                }
            }

            CompleteType.SEYFOT_EXPOSURE -> {
                for (verseParts in partitionedChapter) {
                    val otPasuk = verseParts[0][0]
                    val size = verseParts.size

                    val reysha = retrieveVerse(verseParts[1])
                    var seyfa = retrieveVerse(verseParts[size - 1])
                    if (size == 4) {
                        seyfa = retrieveVerse(verseParts[2]) + " " + seyfa
                    }

                    appendOtPasuk(out = out, spans = spans, otPasuk = otPasuk)

                    appendBlankWithRevealSpanUseCase(
                        out = out,
                        spans = spans,
                        originWord = reysha,
                        color = INDIGO_DARK,
                        typefaceFamily = FONT_SERIF,
                        appendSpaceAfterMakaf = false,
                    )

                    out.append(" ")
                    out.append(
                        getSeyfaAndColon(
                            seyfa = seyfa,
                            newline = eachVerseInNewLine,
                        ),
                    )
                }
            }

            CompleteType.SHUFFLE_VERSES,
            CompleteType.CREATE_COMPLETIONS,
            CompleteType.NEXT_EXPOSURE_VERSE,
            CompleteType.NEXT_EXPOSURE_HALF,
            CompleteType.NEXT_EXPOSURE_WORD,
            CompleteType.LEXICOGRAPHICALLY_SORTING,
            CompleteType.RESET,
                -> Unit
        }

        return SpannableStringKMP(
            charSequence = out.toString(),
            spannedRangeList = spans.toImmutableList(),
        )
    }

    /**
     * Replaces JVM getOtPasuk/getOtPasukAndReysha.
     *
     * In the domain we append plain text (otPasuk + NBSP), and emit a RelativeSize span
     * for the otPasuk range so UI can render it smaller (like the old RelativeSizeSpan(0.5f)).
     */
    private fun appendOtPasuk(
        out: StringBuilder,
        spans: MutableList<SpannedRange>,
        otPasuk: String,
    ) {
        val start = out.length
        out.append(otPasuk)
        val end = out.length

        spans += SpannedRange(
            spanType = SpanType.RelativeSize(proportion = 0.5f),
            textRange = TextRange(start = start, end = end),
        )

        out.append(NO_BREAK_SPACE)
    }

    /**
     * Replaces JVM getColon(newline).
     */
    private fun appendColon(
        out: StringBuilder,
        newline: Boolean,
    ) {
        out.append(COLON_SPLITTER)
        out.append(if (newline) "\n" else " ")
    }

    private fun buildSpannedSegment(
        out: StringBuilder,
        spans: MutableList<SpannedRange>,
        words: List<String>,
    ) {
        val length = words.size
        val numbers = range(startFrom = 0, stopAt = length).toMutableList()

        // Safety guard:
        // The longest verse in Tanach is known to have 43 words.
        // Even though this loop should normally terminate when `numbers` becomes empty,
        // we cap attempts to avoid any accidental infinite loop due to future logic/regex changes.
        val maxAttempts = (length.coerceAtMost(maximumValue = 43)) + 5

        var chosenIndex: Int
        var missingWord: String
        var attempts = 0

        while (true) {
            attempts++

            if (numbers.isEmpty()) {
                Napier.w(
                    "buildSpannedSegment: candidates exhausted; falling back to index=0. " +
                            "wordsCount=$length attempts=$attempts words=$words",
                )
                chosenIndex = 0
                missingWord = words.firstOrNull().orEmpty()
                break
            }

            if (attempts > maxAttempts) {
                val fallbackIndex = numbers.firstOrNull() ?: 0
                Napier.w(
                    "buildSpannedSegment: exceeded maxAttempts=$maxAttempts; falling back. " +
                            "wordsCount=$length attempts=$attempts fallbackIndex=$fallbackIndex words=$words",
                )
                chosenIndex = fallbackIndex
                missingWord = words.getOrElse(chosenIndex) { words.firstOrNull().orEmpty() }
                break
            }

            val random = randomProvider.nextInt(numbers.size)
            chosenIndex = numbers[random]
            missingWord = words[chosenIndex]

            val noTeamim = removeTeamimAndMakafUseCase(missingWord)
            if (noTeamim.matchesAny(conjunctionsWords)) {
                Napier.d(
                    "buildSpannedSegment: skipping conjunction '$missingWord' (normalized='$noTeamim'); " +
                            "remainingCandidates=${numbers.size - 1} attempt=$attempts/$maxAttempts",
                )
                numbers.remove(chosenIndex)
                continue
            }

            // Found a non-conjunction word
            break
        }

        for (i in words.indices) {
            val word = words[i]
            if (i == chosenIndex) {
                appendBlankWithRevealSpanUseCase(
                    out = out,
                    spans = spans,
                    originWord = missingWord,
                    color = INDIGO_DARK,
                    typefaceFamily = FONT_SERIF,
                    appendSpaceAfterMakaf = false,
                )
                if (word.endsWith(MAKAF.toString())) {
                    out.append(" ")
                }
            } else {
                out.append(word)
            }

            if (!word.endsWith(MAKAF.toString())) {
                out.append(" ")
            }
        }

        if (out.isNotEmpty() && out.last() == ' ') {
            out.setLength(out.length - 1)
        }
    }

}
